import { API } from "../../api.js";
import { cashSinceCloseNote, currentCash, getCurrentTab, renderAchievementBadge } from "../../app/shell.js";
import { drawCashChart, drawMarketShareChart } from "../../ui/charts.js";
import { $, cls, esc, fmtMoney, fmtNum, fmtPct } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { svgRadar } from "../../charts/radar.js";

function _companyStage(annualRevenue) {
  if (annualRevenue < 100000) return "Idea / Pre-Revenue";
  if (annualRevenue < 1_000_000) return "Early Stage";
  if (annualRevenue < 10_000_000) return "Growth Stage";
  if (annualRevenue < 100_000_000) return "Scale-up";
  return "Enterprise";
}

function renderChallengeBanner(challenge) {
  const statusLabel = { in_progress: "In Progress", won: "Won", lost: "Lost" }[challenge.status] || challenge.status;
  const p = challenge.progress || {};
  let progressLine = `Month ${p.months_elapsed} of ${challenge.deadline_months}`;
  if (challenge.id === "ipo_sprint") {
    progressLine += p.is_public ? " — public" : ` — ${p.months_remaining} month(s) left to IPO`;
  } else if (challenge.id === "recession_survival") {
    progressLine += ` — cash ${fmtMoney(p.current_cash)}`;
  } else if (challenge.id === "world_number_one") {
    progressLine += ` — currently rank #${p.current_rank} of ${p.total_tracked}`;
  }
  return `
    <div class="challenge-banner challenge-${challenge.status}">
      <div>
        <div class="challenge-title">🏁 ${esc(challenge.name)}</div>
        <div class="challenge-desc">${esc(challenge.description)}</div>
        <div class="challenge-desc" style="margin-top:4px;">${esc(progressLine)}</div>
      </div>
      <div class="challenge-status-tag">${esc(statusLabel)}</div>
    </div>
  `;
}

// Drive (backend drive.py): growth comes from decisions; idle months coast down.
function renderDriveCard(d) {
  const recent = (d.recent || []).slice(0, 4);
  return `
    <div class="drive-card drive-${esc(d.tone)}" title="${esc(d.how)}">
      <div class="drive-head">
        <div>
          <div class="drive-label">Drive</div>
          <div class="drive-verdict">${esc(d.verdict)}</div>
        </div>
        <div class="drive-num">${Math.round(d.drive_pct)}%</div>
      </div>
      <div class="drive-meter" role="meter" aria-valuenow="${Math.round(d.drive_pct)}" aria-valuemin="0" aria-valuemax="100" aria-label="Drive">
        <span style="width:${Math.min(100, d.drive_pct)}%"></span><i style="left:60%" title="Full growth from here"></i>
      </div>
      <div class="drive-facts">
        <span>Core growth <b class="${d.core_growth_pct >= 0 ? "positive" : "negative"}">${d.core_growth_pct >= 0 ? "+" : ""}${d.core_growth_pct.toFixed(2)}%/mo</b> (industry pace ${d.industry_growth_pct.toFixed(2)}%)</span>
        <span>Track record <b>${d.track_record} of ${d.track_cap} months</b></span>
        <span>If you do nothing: <b>${Math.round(d.next_month_drive_pct)}%</b> next month</span>
        ${d.idle_waste_pct > 0.05 ? `<span title="With nobody steering, projects overrun and overhead creeps">Idle waste <b class="negative">${d.idle_waste_pct.toFixed(1)}% of revenue</b></span>` : ""}
      </div>
      ${recent.length ? `<div class="drive-recent">${recent.map((x) => `<span class="pill">M${x.month} · ${esc(x.label)}</span>`).join("")}</div>`
        : `<div class="drive-recent muted">No decisions yet. Launch, price, hire, market or expand to build Drive.</div>`}
    </div>`;
}

async function renderOverview(main, snap) {
  const is = snap.income_statement, bs = snap.balance_sheet, r = snap.ratios;
  const c = window.__company;
  const isNewCompany = snap.month === 0 && !localStorage.getItem("ges_getting_started_dismissed");
  main.innerHTML = `
    <h2 class="panel-title">Overview — Month ${snap.month} ${helpIcon("overview")}</h2>

    ${isNewCompany ? `
    <div class="plain-english-card" id="getting-started-nudge" style="display:flex; justify-content:space-between; align-items:flex-start; gap:14px; border-left-color:var(--brass);">
      <div>
        <strong style="color:var(--brass);">Getting started:</strong> most runs begin one of two ways —
        launch your first product on the <strong>Products</strong> tab to start building real revenue,
        or set a price, hire, or run a campaign. Every decision adds <strong>Drive</strong>: a company left alone
        stops growing and slowly loses customers, so make at least one move before you hit <strong>Next Month →</strong>.
      </div>
      <button id="dismiss-getting-started" style="flex-shrink:0; font-size:12px; padding:5px 10px;">Got it</button>
    </div>
    ` : ""}

    <div id="challenge-status"></div>

    <div id="company-health">Loading…</div>
    <div id="drive-card"></div>

    <h3 style="font-family:var(--font-display); font-size:16px; margin:4px 0 10px;">Priority Alerts</h3>
    <div id="priority-alerts">Loading…</div>

    <div id="achievements-strip" class="achievements-strip"></div>

    <div class="card-grid">
      <div class="card"><div class="card-label">Revenue (month)</div><div class="card-value">${fmtMoney(is.revenue)}</div></div>
      <div class="card"><div class="card-label">Net Income (month)</div><div class="card-value ${cls(is.net_income)}">${fmtMoney(is.net_income)}</div></div>
      <div class="card"><div class="card-label">Cash on Hand</div><div class="card-value">${fmtMoney(currentCash())}</div>${cashSinceCloseNote()}</div>
      <div class="card"><div class="card-label">Total Equity</div><div class="card-value">${fmtMoney(bs.total_equity)}</div></div>
    </div>
    <div style="display:flex; gap:24px; flex-wrap:wrap; align-items:flex-start;">
      <div class="chart-wrap" style="flex:2; min-width:280px;"><div id="cash-chart" class="ck-host"></div></div>
      <div style="flex:1; min-width:260px;">
        <div class="chart-wrap small"><div id="market-share-chart" class="ck-host"></div></div>
        <p class="chart-caption">
          Not real market data — there's no single "total market" number to measure against. This estimates
          how big your revenue looks <em>next to the rivals you actually know about</em> (you, plus every
          active competitor tracked on the Competitors tab), each annualized (monthly revenue × 12). A bigger
          slice means you're outgrowing them, not that you own that % of a real industry.
        </p>
      </div>
    </div>

    <table class="ledger" style="margin-bottom:28px;" id="ceo-dashboard-table">
      <tr class="section"><td class="label">CEO Dashboard</td><td></td></tr>
      <tr><td class="label">Loading…</td><td></td></tr>
    </table>

    <table class="ledger" style="margin-bottom:28px;">
      <tr class="section"><td class="label">Founding Charter</td><td></td></tr>
      <tr><td class="label">Industry</td><td style="font-family:var(--font-body);">${c.industry}</td></tr>
      <tr><td class="label">Headquarters</td><td style="font-family:var(--font-body);">${c.hq_country}</td></tr>
      <tr><td class="label">Funding type</td><td style="font-family:var(--font-body);">${c.funding_type}</td></tr>
      <tr><td class="label">Capital tier</td><td style="font-family:var(--font-body);">${c.capital_tier}</td></tr>
      <tr><td class="label">Founding capital / debt</td><td class="mono">${fmtMoney(c.initial_capital)} / ${fmtMoney(c.initial_debt)}</td></tr>
      <tr><td class="label">Operational scale</td><td style="font-family:var(--font-body);">${c.scale}</td></tr>
      <tr><td class="label">Difficulty / Scenario</td><td style="font-family:var(--font-body);">${esc(c.difficulty || "Standard")} · ${esc(c.scenario || "Custom Founding")}</td></tr>
      ${c.challenge_id ? `<tr><td class="label">Challenge</td><td style="font-family:var(--font-body);">${esc(c.challenge_id)}</td></tr>` : ""}
    </table>

    <table class="ledger">
      <tr><th>Metric</th><th>Value</th></tr>
      <tr><td class="label">Gross Margin</td><td>${fmtPct(r.gross_margin_pct)}</td></tr>
      <tr><td class="label">Net Margin</td><td>${fmtPct(r.net_margin_pct)}</td></tr>
      <tr><td class="label">Debt / Equity</td><td>${r.debt_to_equity === null ? "—" : fmtNum(r.debt_to_equity)}</td></tr>
      <tr><td class="label">Credit Rating</td><td>${r.credit_rating}</td></tr>
      <tr><td class="label">Brand Score</td><td>${r.brand_score}/100</td></tr>
    </table>
  `;
  drawCashChart();
  if (isNewCompany) {
    $("dismiss-getting-started").addEventListener("click", () => {
      localStorage.setItem("ges_getting_started_dismissed", "1");
      $("getting-started-nudge").remove();
    });
  }

  fetch(`${API}/drive`).then((x) => x.json()).then((d) => {
    if (getCurrentTab() === "overview" && $("drive-card")) $("drive-card").innerHTML = renderDriveCard(d);
  }).catch(() => {});
  const [countries, products, deals, workforce, execs, alerts, achievements, competitors, health, challenge] = await Promise.all([
    fetch(`${API}/countries`).then((x) => x.json()),
    fetch(`${API}/products`).then((x) => x.json()),
    fetch(`${API}/deals`).then((x) => x.json()),
    fetch(`${API}/workforce`).then((x) => x.json()),
    fetch(`${API}/executives`).then((x) => x.json()),
    fetch(`${API}/alerts`).then((x) => x.json()),
    fetch(`${API}/achievements`).then((x) => x.json()),
    fetch(`${API}/competitors`).then((x) => x.json()),
    fetch(`${API}/company-health`).then((x) => x.json()),
    fetch(`${API}/challenge`).then((x) => x.json()),
  ]);
  // The player may have switched tabs while this loaded; its elements are gone.
  if (getCurrentTab() !== "overview" || !$("company-health")) return;

  $("challenge-status").innerHTML = challenge.active ? renderChallengeBanner(challenge) : "";

  $("company-health").innerHTML = `
    <div style="display:flex; gap:16px; flex-wrap:wrap; align-items:stretch; margin-bottom:20px;">
      <div class="health-banner health-${health.label.toLowerCase()}" style="flex:1; min-width:200px;">
        <div class="health-label">Company Health</div>
        <div class="health-score">${health.score}<span style="font-size:16px;opacity:0.7;">/100</span></div>
        <div class="health-tag">${esc(health.label)}</div>
      </div>
      ${health.components ? `
      <div class="chart-wrap" style="flex:1; min-width:220px; height:auto; padding:14px;">
        <div style="font-family:var(--font-mono); font-size:10px; letter-spacing:0.08em; color:var(--text-low); text-transform:uppercase; margin-bottom:4px;">Stakeholder Radar</div>
        <div id="health-radar-chart"></div>
      </div>` : ""}
    </div>
  `;
  // the game's own SVG radar (ui/kit.js), the five inputs behind the health score (2.7: was a hand-drawn canvas)
  if (health.components) {
    const c = health.components;
    $("health-radar-chart").innerHTML = svgRadar([["Board", c.board_confidence], ["Investors", c.investor_sentiment], ["Runway", c.cash_runway],
      ["Profit", c.profitability], ["Morale", c.morale]].map(([axis, v]) => ({ axis, weight: Math.round(Math.max(0, Math.min(100, v ?? 0))) })), { size: 200 });
  }

  $("priority-alerts").innerHTML = alerts.length ? alerts.map((a) => `
    <div class="alert-card ${a.severity}">
      <div>
        <div class="alert-category">${esc(a.category)}</div>
        <div class="alert-message">${esc(a.message)}</div>
      </div>
      <button class="alert-jump" data-jump="${esc(a.tab)}">View →</button>
    </div>
  `).join("") : `<div class="all-clear">✓ All clear — no priority alerts this month.</div>`;
  $("priority-alerts").querySelectorAll("[data-jump]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const tabBtn = document.querySelector(`.tab[data-tab="${btn.dataset.jump}"]`);
      if (tabBtn) tabBtn.click();
    });
  });

  $("achievements-strip").innerHTML = achievements.map((a) => renderAchievementBadge(a)).join("");

  drawMarketShareChart(is.revenue, competitors);

  const activeProducts = products.filter((p) => p.active);
  const signedPartnerships = deals.filter((d) => d.status === "signed");
  const stage = _companyStage(is.revenue * 12);
  const totalEmployees = workforce.total_headcount; // already counts executives (engine.people_count, 2.7)
  $("ceo-dashboard-table").innerHTML = `
    <tr class="section"><td class="label">CEO Dashboard</td><td></td></tr>
    <tr><td class="label">Employees</td><td class="mono">${totalEmployees.toLocaleString("en-US")}</td></tr>
    <tr><td class="label">Countries Operating In</td><td class="mono">${countries.length} — ${countries.join(", ")}</td></tr>
    <tr><td class="label">Active Products</td><td class="mono">${activeProducts.length}</td></tr>
    <tr><td class="label">Company Stage</td><td class="mono">${stage}</td></tr>
    <tr><td class="label">Strategic Partnerships</td><td class="mono">${signedPartnerships.length ? signedPartnerships.map((d) => d.company_name).join(", ") : "None yet"}</td></tr>
    <tr><td class="label">Founder Ownership</td><td class="mono">${r.founder_ownership_pct != null ? r.founder_ownership_pct.toFixed(1) + "%" : "100.0%"}</td></tr>
  `;
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderOverview = renderOverview;

export { renderChallengeBanner, renderDriveCard };
